import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Terminal, FlaskConical, AlertTriangle, Search, Play, Loader2,
} from 'lucide-react';
import { FileCard } from './FileCard';
import { DeliveryCard } from './DeliveryCard';
import { ArchitectDeliveryCard } from './ArchitectDeliveryCard';
import type { Turn, UICard } from './types';
import { apiService } from '../../services/api';
import { toast } from 'sonner';

export const UserBubble: React.FC<{ turn: Turn }> = ({ turn }) => (
  <motion.div
    initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
    className="flex justify-end"
    data-testid="chat-turn-message"
  >
    <div
      className="rounded-2xl rounded-br-md px-4 py-2 max-w-[85%] sm:max-w-[70%] text-sm"
      style={{
        background: 'hsl(var(--accent) / 0.16)',
        border: '1px solid hsl(var(--accent) / 0.35)',
        color: 'hsl(var(--text))',
        boxShadow: '0 4px 22px hsl(var(--accent) / 0.15)',
      }}
    >
      <div className="whitespace-pre-wrap break-words">{turn.content}</div>
      {turn.attachments && turn.attachments.length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/20 flex flex-col gap-1">
          {turn.attachments.map((a, i) => (
            <div key={i} className="text-[11px] opacity-90 inline-flex items-center gap-1">
              <span className="opacity-80">📎</span>
              <span className="truncate">{a.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  </motion.div>
);

export const EngineMessage: React.FC<{ turn: Turn }> = ({ turn }) => {
  // Sprint Cierre Final v1.27 · botón "Escuchar" en cada respuesta.
  // Cualquier mensaje del Arquitecto o del engine se puede reproducir
  // con la voz elegida por el usuario (config en /perfil/voz-audio).
  const [playing, setPlaying] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const speak = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
      return;
    }
    try {
      setPlaying(true);
      const url = await apiService.getTTSUrl?.(turn.content || '');
      if (!url) throw new Error('no url');
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPlaying(false); audioRef.current = null; };
      audio.onerror = () => { setPlaying(false); audioRef.current = null; };
      await audio.play();
    } catch {
      setPlaying(false);
      audioRef.current = null;
      toast.error('No se pudo reproducir el audio.');
    }
  };
  const label = turn.role === 'architect' ? 'Arquitecto' : 'Nexus IA';
  return (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
    className="flex justify-start items-start gap-2.5" data-testid="chat-turn-message"
  >
    <div
      className="w-7 h-7 rounded-lg grid place-items-center shrink-0 mt-0.5"
      style={{
        background: 'hsl(var(--accent) / 0.14)',
        border: '1px solid hsl(var(--accent) / 0.4)',
        color: 'hsl(var(--accent))',
        boxShadow: '0 0 12px hsl(var(--accent) / 0.25)',
      }}
      aria-label={label}
    >
      <svg viewBox="0 0 20 20" className="w-3.5 h-3.5" fill="currentColor" aria-hidden>
        <path d="M10 2l1.6 4.4L16 8l-4.4 1.6L10 14l-1.6-4.4L4 8l4.4-1.6L10 2z"/>
      </svg>
    </div>
    <div className="max-w-[85%] sm:max-w-[70%] text-zinc-200 text-sm whitespace-pre-wrap break-words leading-relaxed group">
      <div className="text-[10px] uppercase tracking-widest opacity-60 mb-0.5 flex items-center gap-2"
           style={{ color: 'hsl(var(--accent))' }}>
        <span>{label}</span>
        <button
          onClick={speak}
          className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 text-[10px] hover:opacity-100"
          style={{ color: 'hsl(var(--accent))' }}
          data-testid="chat-listen-btn"
          aria-label={playing ? 'Detener reproducción' : 'Escuchar respuesta'}
          title={playing ? 'Detener' : 'Escuchar'}
        >
          {playing ? '⏸' : '🔊'} <span>Escuchar</span>
        </button>
      </div>
      {turn.content}
    </div>
  </motion.div>
  );
};

export const ErrorBubble: React.FC<{ turn: Turn; workspaceId?: string }> = ({ turn, workspaceId }) => {
  const detail = (turn.detail || {}) as any;
  const canResume = detail.action === 'resume_build' && !!detail.can_resume;
  const [resuming, setResuming] = useState(false);
  const [resumed, setResumed] = useState(false);

  // Progress metrics — only meaningful for pause turns
  const completed: string[] = detail.completed_modules || [];
  const pendingModule: string | undefined = detail.pending_module;
  // We don't know the total planned modules count here directly; if the
  // orchestrator didn't include it, estimate as completed + 1 pending.
  const totalEstimate = (detail.total_modules as number)
    || (completed.length + (pendingModule ? 1 : 0)) || 1;
  const pct = Math.max(0, Math.min(100, Math.round((completed.length / totalEstimate) * 100)));

  const resume = async () => {
    if (!workspaceId) return;
    setResuming(true);
    try {
      await apiService.resumeBuild(workspaceId);
      setResumed(true);
      toast.success('Reanudando construcción…');
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Error';
      toast.error(`No pude reanudar: ${msg}`);
    } finally {
      setResuming(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="flex justify-start" data-testid="chat-turn-error"
    >
      <div className="max-w-[85%] sm:max-w-[70%] bg-rose-500/10 border border-rose-400/20 text-rose-200 rounded-2xl rounded-bl-md px-4 py-3 text-sm">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="whitespace-pre-wrap break-words">{turn.content}</div>
        </div>

        {canResume && (
          <div
            className="mt-3 pt-3 border-t border-rose-400/20"
            data-testid="chat-pause-progress"
          >
            <div className="flex items-center justify-between text-[11px] mb-1.5 opacity-80">
              <span>Progreso guardado</span>
              <span className="font-mono">{pct}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden mb-2">
              <div
                className="h-full transition-all"
                style={{
                  width: `${pct}%`,
                  background: 'linear-gradient(90deg, #34d399, #10b981)',
                }}
              />
            </div>
            {completed.length > 0 && (
              <div className="text-[11px] opacity-85 mb-1">
                <span className="text-emerald-300">✓</span> Terminados:
                <span className="ml-1 font-mono">{completed.join(', ')}</span>
              </div>
            )}
            {pendingModule && (
              <div className="text-[11px] opacity-85">
                <span className="text-amber-300">◐</span> Pausado en:
                <span className="ml-1 font-mono">{pendingModule}</span>
              </div>
            )}
          </div>
        )}

        {canResume && !resumed && (
          <div className="mt-3 pt-3 border-t border-rose-400/20 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={resume}
              disabled={resuming}
              data-testid="chat-resume-build-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition disabled:opacity-50"
              style={{
                background: 'hsl(var(--accent))',
                color: '#0a0f10',
                boxShadow: '0 4px 14px hsl(var(--accent) / 0.35)',
              }}
            >
              {resuming ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Play className="w-3.5 h-3.5" />
              )}
              {resuming ? 'Reanudando…' : 'Continuar'}
            </button>
            <a
              href="https://app.emergent.sh/profile"
              target="_blank" rel="noopener noreferrer"
              className="text-[11px] underline text-rose-200/80 hover:text-rose-100"
            >
              Recargar Universal Key
            </a>
          </div>
        )}
        {resumed && (
          <div
            className="mt-3 pt-3 border-t border-rose-400/20 text-[12px] text-emerald-300"
            data-testid="chat-resumed-badge"
          >
            ✓ Reanudando desde el módulo pausado. Sigue el progreso arriba.
          </div>
        )}
      </div>
    </motion.div>
  );
};

export const QuestionBubble: React.FC<{ turn: Turn }> = ({ turn }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
    className="flex justify-start" data-testid="chat-turn-question"
  >
    <div className="max-w-[85%] sm:max-w-[70%] bg-amber-500/10 border border-amber-400/20 text-amber-100 rounded-2xl rounded-bl-md px-4 py-2 text-sm whitespace-pre-wrap break-words">
      {turn.content}
    </div>
  </motion.div>
);

export const BitacoraLine: React.FC<{ turn: Turn }> = ({ turn }) => {
  // v1.4 · Timeline "ENGINE RUNNING" row per Image 2 reference.
  const ts = (turn as any).created_at
    ? new Date((turn as any).created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    : '';
  return (
    <motion.div
      initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }}
      className="flex items-start gap-3 pl-3"
      data-testid="chat-turn-bitacora"
    >
      <span
        className="mt-1.5 shrink-0 block w-2 h-2 rounded-full"
        style={{
          background: 'hsl(var(--accent))',
          boxShadow: '0 0 12px hsl(var(--accent) / 0.7)',
        }}
      />
      <div className="min-w-0 flex-1 flex items-baseline gap-2">
        <span className="text-[10px] uppercase tracking-widest opacity-50 font-mono tabular-nums">
          {ts}
        </span>
        <span className="text-[13px] leading-snug" style={{ color: 'hsl(var(--text) / 0.9)' }}>
          {turn.content}
        </span>
      </div>
    </motion.div>
  );
};

export const StateLine: React.FC<{ turn: Turn }> = ({ turn }) => (
  <div className="text-center text-[11px] text-zinc-600 italic px-4"
       data-testid="chat-turn-state">
    {turn.content}
  </div>
);

export const SearchPill: React.FC<{ card: UICard }> = ({ card }) => (
  <div className="flex justify-start" data-testid="chat-turn-bitacora-card">
    <div className="inline-flex items-center gap-2 text-xs text-zinc-400 bg-white/[0.02] border border-white/[0.05] rounded-full px-3 py-1.5">
      <Search className="w-3 h-3 text-violet-300" />
      <span className="font-mono">{card.label}</span>
    </div>
  </div>
);

export const TransientCommand: React.FC<{ card: UICard }> = ({ card }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.96 }}
    className="inline-flex items-center gap-2 bg-black/40 border border-white/10 rounded-full px-3 py-1.5 max-w-full"
  >
    {card.type === 'test' ? (
      <FlaskConical className="w-3.5 h-3.5 text-cyan-300 animate-pulse" />
    ) : (
      <Terminal className="w-3.5 h-3.5 text-cyan-300 animate-pulse" />
    )}
    <span className="text-xs font-mono text-zinc-300 truncate">
      {card.command || card.label}
    </span>
  </motion.div>
);

/* TurnRow dispatches a turn to the right sub-component */
export const TurnRow: React.FC<{
  turn: Turn;
  workspaceId: string;
  onOpenFile: (p: string, baseline?: string) => void;
  onShowModifiedFiles: () => void;
  onRegression: () => void;
  onRollback: () => void;
  onShowSummary: () => void;
  onExport: () => void;
  onPreview: () => void;
  onDeploy?: () => void;
}> = (props) => {
  const t = props.turn;
  const card = t.detail?.card as UICard | undefined;

  if (t.role === 'user' && t.kind === 'message') return <UserBubble turn={t} />;
  // Sprint Arquitecto IA · Delivery Card emitida por el Arquitecto.
  if (t.kind === 'delivery_card') {
    // v1.31 · Preview routing (bug fix "El preview estará disponible
    // cuando la publicación termine.")
    // Priority order:
    //   1. Card's own `preview_url` (works even before publish for
    //      generated scaffolds AND for the Nexus clone).
    //   2. Existing deployment record.
    //   3. Fallback toast.
    const openPreview = async () => {
      const cardDetail = (t as any).detail || (t as any).delivery || {};
      // Sprint P0.3 v1.47 · CROSS-WORKSPACE PROTECTION.
      // Defense in depth: preferimos SIEMPRE construir el preview URL
      // desde el workspace_id de la URL actual (o de refs) en lugar de
      // confiar en `preview_url` que podría venir contaminado de una
      // card legacy (bug reportado: card en workspace X abría cronómetro
      // de workspace Y). Solo caemos a `preview_url` explícito si NO
      // hay workspace_id detectable.
      const urlWsId = (() => {
        try {
          const m = window.location.pathname.match(/\/w\/([^\/?#]+)/);
          return m ? m[1] : '';
        } catch { return ''; }
      })();
      const wsIdForPreview = urlWsId
        || (t.refs as any)?.workspace_id
        || (cardDetail && cardDetail.workspace_id)
        || '';
      const cardWsId = (t.refs as any)?.workspace_id
        || (cardDetail && cardDetail.workspace_id)
        || '';
      // Detectamos contaminación cross-workspace: card guardada dentro
      // de workspace A pero con preview_url apuntando a workspace B.
      // En ese caso ignoramos preview_url y forzamos el workspace de la
      // URL actual.
      const rawPreviewUrl: string | undefined =
        cardDetail.preview_url ||
        cardDetail.self_dev_working_url ||
        cardDetail.public_url;
      const previewLooksContaminated = (() => {
        if (!rawPreviewUrl || !urlWsId) return false;
        try {
          const m = rawPreviewUrl.match(/\/workspaces\/([^\/?#]+)\//);
          if (m && m[1] && m[1] !== urlWsId) return true;
        } catch { /* noop */ }
        return false;
      })();
      const shouldForceUrlWs = Boolean(
        urlWsId && (previewLooksContaminated
          || (cardWsId && cardWsId !== urlWsId))
      );
      let previewUrl: string | undefined = rawPreviewUrl;
      if (shouldForceUrlWs || (!previewUrl && wsIdForPreview)) {
        const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        previewUrl = `${backend}/api/workspaces/${wsIdForPreview}/runtime/proxy/?nexus_edit=1`;
      }
      if (previewUrl) {
        try {
          const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
          let target = previewUrl.startsWith('http')
            ? previewUrl
            : `${backend}${previewUrl}`;
          const token = localStorage.getItem('nexus_token') || '';
          if (token && !target.includes('token=')) {
            target += (target.includes('?') ? '&' : '?') + `token=${encodeURIComponent(token)}`;
          }
          window.open(target, '_blank', 'noopener');
          return;
        } catch { /* fall through */ }
      }
      try {
        const wsId = wsIdForPreview;
        if (wsId) {
          const dep = await (apiService as any).getWorkspaceDeploy?.(wsId);
          if (dep?.deploy_url) { window.open(dep.deploy_url, '_blank'); return; }
        }
      } catch { /* fall through */ }
      toast('El preview estará disponible cuando la publicación termine.');
    };
    // v1.31 · Publish routing for self-dev workspaces goes straight to
    // the sandbox API instead of navigating to /w/:id?publish=1 which
    // caused the loop the user reported.
    const openPublish = async () => {
      const cardDetail = (t as any).detail || {};
      const isSelfDev = Boolean(cardDetail.nexus_clone_edit || cardDetail.self_dev_working_url);
      // Sprint P0.3 v1.47 · Fallback ORDENADO al workspace_id:
      //   1) refs.workspace_id de la card (preferido)
      //   2) detail.workspace_id (algunas cards viejas guardaron ahí)
      //   3) URL actual /w/{id} (siempre correcto si estamos dentro del workspace)
      //   4) turn.workspace_id (algunos turnos legacy)
      // El usuario vio "No pude identificar el proyecto a publicar." estando
      // DENTRO del workspace (con ID visible en el header). La card era
      // legacy sin refs.workspace_id. La URL fallback resuelve el 99% de casos.
      const urlWsId = (() => {
        try {
          const m = window.location.pathname.match(/\/w\/([^\/?#]+)/);
          return m ? m[1] : '';
        } catch { return ''; }
      })();
      const wsId = (t.refs as any)?.workspace_id
        || (cardDetail && cardDetail.workspace_id)
        || urlWsId
        || (t as any).workspace_id
        || '';
      if (isSelfDev) {
        try {
          const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
          const token = localStorage.getItem('nexus_token') || '';
          const r = await fetch(`${backend}/api/self/publish-sandbox`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
          const j = await r.json().catch(() => ({}));
          if (r.ok && j.ok) {
            toast.success('Publicado al Sandbox correctamente.');
            const sbxUrl = `${backend}/api/self/preview/sandbox/`;
            setTimeout(() => window.open(sbxUrl, '_blank', 'noopener'), 600);
            return;
          }
          toast.error(j?.detail || 'No se pudo publicar al Sandbox.');
          return;
        } catch (e: any) {
          toast.error('No se pudo publicar al Sandbox.');
          return;
        }
      }
      // Sprint P0.1 v1.44 · Publish desde CUALQUIER contexto (inline
      // card outside workspace, home, etc.) sin exigir "abrir el
      // workspace primero". Antes esta rama redirigía a
      // `/w/{id}?publish=1` — el usuario perdía el contexto y sentía
      // que la publicación pedía un paso extra. Ahora hacemos publish
      // directo con toast progresivo + abrir la URL en nueva pestaña.
      if (!wsId) {
        // Sprint P0.7 v1.50 · Última defensa: si TODAS las fuentes de
        // workspace_id fallaron (URL, refs, detail, turn), intentamos
        // recuperar el workspace más reciente del usuario. Esto elimina
        // el error "No pude identificar el proyecto" en cards huérfanas.
        try {
          const { apiService } = await import('../../services/api');
          const list: any = await (apiService as any).api?.get?.('/workspaces');
          const wss: any[] = Array.isArray(list?.data) ? list.data : (list?.data?.workspaces || []);
          const sorted = wss.sort((a: any, b: any) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime());
          if (sorted[0]?.id) {
            const recovered = sorted[0].id;
            const proceed = window.confirm(
              `No pude identificar el proyecto exacto de esta card.\n\n`
              + `¿Quieres publicar tu workspace más reciente («${sorted[0].name || recovered.slice(0, 12)}»)?`
            );
            if (proceed) {
              const publishToast2 = toast.loading('Publicando tu aplicación…');
              try {
                const r: any = await apiService.publishWorkspace(recovered, 'auto');
                const url = r?.url || r?.public_url || r?.deployment_url;
                if (url) {
                  toast.success('Publicado — abre la URL en una nueva pestaña.', { id: publishToast2 });
                  setTimeout(() => { try { window.open(url, '_blank', 'noopener'); } catch { /* noop */ } }, 400);
                } else {
                  toast.error('Publicación completada pero sin URL pública.', { id: publishToast2 });
                }
              } catch (e2: any) {
                toast.error(e2?.message || 'La publicación no pudo completarse.', { id: publishToast2 });
              }
              return;
            }
          }
        } catch { /* fall through */ }
        toast.error('No pude identificar el proyecto a publicar.');
        return;
      }
      const publishToast = toast.loading('Publicando tu aplicación…');
      try {
        const { apiService } = await import('../../services/api');
        const r: any = await apiService.publishWorkspace(wsId, 'auto');
        const url = r?.url || r?.public_url || r?.deployment_url;
        if (!url) {
          toast.error('Publicación completada pero sin URL pública.', { id: publishToast });
          return;
        }
        toast.success('¡Publicada!', { id: publishToast });
        try {
          window.dispatchEvent(new CustomEvent('nexus:publish-completed', {
            detail: { workspace_id: wsId, url },
          }));
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('nexus:publish-thumbnail-ready', {
              detail: { workspace_id: wsId },
            }));
          }, 30000);
        } catch { /* noop */ }
        window.open(url, '_blank', 'noopener');
      } catch (e: any) {
        toast.error(e?.message || 'La publicación no pudo completarse.', { id: publishToast });
      }
    };
    return (
      <div className="flex justify-start" id={`turn-${t.id}`}
           data-testid="chat-turn-architect-delivery">
        <ArchitectDeliveryCard turn={t} onPreview={openPreview} onPublish={openPublish} />
      </div>
    );
  }
  if (t.role === 'engine' && t.kind === 'result') {
    return (
      <div className="flex justify-start" id={`turn-${t.id}`}
           data-testid="chat-turn-result">
        <DeliveryCard
          turn={t}
          workspaceId={props.workspaceId}
          onPreview={props.onPreview}
          onExport={props.onExport}
          onShowModifiedFiles={props.onShowModifiedFiles}
          onRegression={props.onRegression}
          onRollback={props.onRollback}
          onShowSummary={props.onShowSummary}
          onDeploy={props.onDeploy}
        />
      </div>
    );
  }
  if (t.kind === 'error') return <ErrorBubble turn={t} workspaceId={props.workspaceId} />;
  if (t.kind === 'question') return <QuestionBubble turn={t} />;
  if (t.role === 'engine' && t.kind === 'message') return <EngineMessage turn={t} />;
  // Sprint Cierre Final v1.27 · role='architect' se renderiza como
  // EngineMessage (mismo look) pero con etiqueta "Arquitecto".
  if (t.role === 'architect' && (t.kind === 'message' || t.kind === 'result')) {
    return <EngineMessage turn={t} />;
  }
  if (t.kind === 'bitacora' && card && !card.transient) {
    if (card.type === 'file' && card.path) {
      // For "edited" cards, fetch the original via baseline from refs if present
      const baseline = t.refs?.baseline_content as string | undefined;
      return (
        <div className="flex justify-start" data-testid="chat-turn-bitacora-card">
          <FileCard
            card={card}
            onOpen={() => props.onOpenFile(card.path!, baseline)}
          />
        </div>
      );
    }
    if (card.type === 'search') return <SearchPill card={card} />;
  }
  if (t.kind === 'bitacora') {
    if (!t.content) return null;
    // ── Sprint v9 chat cleanup ────────────────────────────────────
    // Hide internal engine chatter from the main feed. The user only
    // sees narrative progress messages; deep phase logs remain in
    // /api/observability for debugging but don't clutter the chat.
    const phase = (t.detail?.phase || '') as string;
    const INTERNAL_PHASE_PREFIXES = [
      'runtime.',            // runtime start/stop internals
      'single_page.',        // spa write internals
      'single_page_modify.', // patch internals except .done which is user-facing
      'build.',              // orchestrator internals
      'spec_inference.',
      'snapshot.',
      'scaffold.',           // scaffold detail — the *.done result already covers it
      'scaffold_',           // v24.2 — legacy underscored phases (scaffold_start, scaffold_done)
      'analyze.',
      'preview.autostart',
      'autonomous_loop.',    // v24 — all autonomous retry / promote / sandbox chatter hidden
      'wire_up.',
      'orchestrator.',
      'planner.',
      'specialist.',
      'reflect.',
      'frontend.',           // v24 — frontend.autobuild.* internal
      'module.',             // v24 — orchestrator module.* internals
      'stack.',              // v24.2 — stack classifier internals
      'spec.',               // v24.2 — spec inference internals
      'immediate',           // v24.2 — "Pensando cómo construirlo…" ack
    ];
    const KEEP_EVEN_INTERNAL = new Set([
      'single_page_modify.done',   // "cambios aplicados · N bloques" — useful
      'runtime.started',           // "preview listo" — useful
      'build.skipped_static',      // "detecté landing_page" — useful
    ]);
    const isInternal =
      phase &&
      INTERNAL_PHASE_PREFIXES.some((p) => phase.startsWith(p)) &&
      !KEEP_EVEN_INTERNAL.has(phase);
    if (isInternal) return null;
    return <BitacoraLine turn={t} />;
  }
  if (t.kind === 'state_change') return <StateLine turn={t} />;
  return null;
};
